import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../components/EditProductDescription/EditProductDescription.css";
import Breadcrumb from "../../components/Common/Bredcrump";
import EmailHeaderImage from "../../components/Email/EmailHeaderImage";
import EmailDetailsContainer from "../../components/Email/EmailDetailsContainer";
import CreateEmailLeft from "../../components/Email/CreateEmailLeft";
import Loader from "../../components/Common/Loader";
import {
  EmailStyleProvider,
  fontOptions,
} from "../../components/Context/EmailStyleContext";
import { ExternalLink, Settings, X } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import {
  EmailSectionProvider,
  useEmailSections,
} from "../../components/Context/EmailSectionContext";
import axiosInstance from "../../utils/api"; // adjust path as needed
import AddEmailSidebar from "../../components/Email/AddEmailSidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChromePicker } from "react-color";
import { BiLaptop, BiMobile } from "react-icons/bi";
import LocalDraftModal from "../../components/EditProductDescription/LocalDraftModal";

const CreateEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const templateName = location.state?.templateName || "";
  const previewImage = location.state?.previewImage || "";
  const previewUrl = location.state?.previewUrl || "";
  const templateId = location.state?.templateId || "";
  // Email data state - supports both array (from context) and object (legacy) formats
  const [emailData, setEmailData] = useState(() => {
    // If we have initial sections from location state, use them
    if (Array.isArray(location.state?.emailData)) {
      return location.state.emailData;
    }
    return [];
  });
  const [streamingSections, setStreamingSections] = useState([]);
  const [savedDraft, setSavedDraft] = useState(false);
  const uid = location.state?.uid || "";
  // We're not using the sections from context here since we'll extract them from data
  // Email data state
  const extractSections = (data) => {
    if (!data) return [];
    // If data has a non-empty 'sections' array, use it
    if (Array.isArray(data.sections) && data.sections.length > 0)
      return data.sections;
    // Otherwise, extract all valid section objects (with component_name)
    return Object.values(data).filter(
      (section) =>
        section &&
        typeof section === "object" &&
        section.component_name &&
        Object.keys(section).length > 0 // not empty
    );
  };

  const handleEmailChunk = (chunk) => {
    setStreamingSections((prev) => [...prev, chunk]);
    // Hide loader as soon as first chunk arrives
  };

  const clearStreamingSections = () => setStreamingSections([]);

  const [initialSections, setInitialSections] = useState(() => {
    const extracted = extractSections(location.state?.emailData);
    return extracted.length > 0 ? extracted : [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // Style settings
  const [primaryColor, setPrimaryColor] = useState("#333333");
  const [secondaryColor, setSecondaryColor] = useState("#a1894c");
  const [fontFamily, setFontFamily] = useState(fontOptions[0].value);
  const [showStyleSettings, setShowStyleSettings] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [styleVersion, setStyleVersion] = useState(0);
  const [showLocalDraftModal, setShowLocalDraftModal] = useState(false);
  const [draftTemplateName, setDraftTemplateName] = useState(templateName || "");
  
  // Refs for clicking outside to close color picker
  const colorPickerRef = useRef(null);
  const styleSettingsRef = useRef(null);

  // Update style version whenever a style changes
  useEffect(() => {
    setStyleVersion((prev) => prev + 1);
  }, [primaryColor, secondaryColor, fontFamily]);

  // Test data for initial load
  const testEmailData = {
    "banner-section": {
      component_name: "Banner",
      title: "Hey *|FNAME|*!",
      description:
        "This is a quick reminder that we're still saving your cart at have a 10% discount waiting for you!",
      image:
        "https://via.placeholder.com/600x300/222222/ffffff?text=Banner+Image",
    },
  };

  // Set initial test data if none provided (only for legacy object format)
  useEffect(() => {
    // Only set test data if initialSections is empty
    const isEmpty = Array.isArray(initialSections) 
      ? initialSections.length === 0 
      : Object.keys(initialSections).length === 0;
    
    if (isEmpty && !Array.isArray(emailData)) {
      setEmailData(testEmailData);
    }
  }, []);

  const handleUpdateEmailData = (updatedData) => {
    // If updatedData is an array (from EmailSectionContext), use it directly
    // This preserves section IDs for proper image handling
    if (Array.isArray(updatedData)) {
      console.log("Received array data, setting directly:", updatedData);
      setEmailData(updatedData);
      return;
    }
    setEmailData(updatedData);
  };

  const [occasion, setOccasion] = useState("");
  const [furtherDetails, setFurtherDetails] = useState("");

  // Add click outside handler for style settings panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        styleSettingsRef.current &&
        !styleSettingsRef.current.contains(event.target) &&
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setShowStyleSettings(false);
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleColorPicker = (type) => {
    setActiveColorPicker(type);
    setShowColorPicker(!showColorPicker);
  };

  const handleColorChange = (color) => {
    if (activeColorPicker === "primary") {
      setPrimaryColor(color.hex);
    } else if (activeColorPicker === "secondary") {
      setSecondaryColor(color.hex);
    }
  };

  const toggleStyleSettings = () => {
    setShowStyleSettings(!showStyleSettings);
  };

  const handleGenerateEmail = () => {
    setShowProductDetails(true);
  };

  // ✅ Call the hook at the top level
  const { sections } = useEmailSections();

  useEffect(() => {
    console.log("Email sections updated:", sections);
  }, [sections]);

  const handlePreview = (mode) => {
    if (savedDraft) {
    navigate("/skills/email/preview-email", {
      state: {
        emailData: sections,
        primaryColor,
        secondaryColor,
        fontFamily,
        mode: mode,
      },
    }); 
  } else {
    toast.error("Please save the draft first");
  }
  };

  const convertToArray = (data) => {
    // If already an array, return as-is (preserving section IDs)
    if (Array.isArray(data)) {
      return data;
    }
    // If object, convert to array (legacy support)
    return Object.keys(data)
      .filter((key) => !isNaN(key))
      .map((key) => data[key]);
  };

  // Show the draft modal to let user enter name or choose update/create new
  const handleShowDraftModal = () => {
    setShowLocalDraftModal(true);
  };

  // Handle local draft modal confirmation (update existing or create new)
  const handleLocalDraftConfirm = async (draftChoice, newDraftTitle) => {
    console.log("Local draft modal confirmed:", draftChoice, newDraftTitle);
    
    if (draftChoice === "update") {
      // Update existing draft with current name
      await performSaveAsDraft(draftTemplateName, true);
    } else if (draftChoice === "new") {
      // Create new draft with new name
      console.log("Creating new email draft with title:", newDraftTitle);
      setDraftTemplateName(newDraftTitle);
      await performSaveAsDraft(newDraftTitle, true);
    }
    
    // Close the modal
    setShowLocalDraftModal(false);
  };

  // Perform the actual save operation
  const performSaveAsDraft = async (nameToSave, showSuccessToast = true) => {
    setIsSavingDraft(true);
    try {
     
      const formData = new FormData();

      const modifiedEmailData = convertToArray(emailData);

      console.log(streamingSections);
      console.log(location.state);
      console.log(emailData);

      // 1. foray_response (stringified JSON)
      formData.append(
        "email_template[foray_response]",
        JSON.stringify({
          data: modifiedEmailData, // or your email sections object
          styleSettings: {
            primaryColor,
            secondaryColor,
            fontFamily,
          },
        })
      );

      // 2. name - use the passed name
      formData.append("email_template[name]", nameToSave || "Draft Email");

      // 3. tone
      formData.append("email_template[tone]", tone);

      // 4. preview_url
      formData.append("email_template[preview_url]", previewUrl);

      // 5. occasion
      formData.append("email_template[occasion]", occasion);

      // 6. further_details
      formData.append("email_template[further_details]", furtherDetails);

      // 7. status
      formData.append("email_template[status]", "draft");

      // 8. uid
      formData.append("email_template[uid]", uid);

      // Helper to convert base64 to File
      function base64ToFile(base64String, filename, mimeType) {
        const byteString = atob(base64String.split(",")[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        return new File([ab], filename, { type: mimeType });
      }

      // Helper to process and append an image to FormData
      const processAndAppendImage = (imageValue, imageName, sectionIndex) => {
        if (!imageValue) return;
        
        let imageFile = imageValue;
        
        // Handle File objects directly
        if (imageValue instanceof File) {
          formData.append("email_template[images][]", imageFile);
          formData.append("email_template[image_names][]", imageName);
          return;
        }
        
        // Handle base64 images
        if (typeof imageValue === "string" && imageValue.startsWith("data:image/")) {
          const mimeMatch = imageValue.match(/^data:(image\/[a-zA-Z]+);base64,/);
          const mimeType = mimeMatch ? mimeMatch[1] : "image/png";
          const extension = mimeType.split("/")[1];
          const filename = `${imageName}_${sectionIndex}.${extension}`;
          imageFile = base64ToFile(imageValue, filename, mimeType);
          formData.append("email_template[images][]", imageFile);
          formData.append("email_template[image_names][]", imageName);
          return;
        }
        
        // Skip regular URL images - they're already stored on server
        if (typeof imageValue === "string" && !imageValue.startsWith("data:")) {
          console.log("Skipping already-uploaded image URL:", imageValue);
          return;
        }
      };

      // 9. images and image_names
      // Use section.id as unique identifier to prevent conflicts between multiple sections of same type
      for (let i = 0; i < modifiedEmailData.length; i++) {
        const section = modifiedEmailData[i];
        // Use section.id for unique naming (prevents conflicts between multiple sections of same component type)
        const sectionId = section?.id || `section_${i}`;
  
        // Handle main image field
        if (section?.image) {
          processAndAppendImage(section.image, sectionId, i);
        }
        
        // Handle Title_4Img component's multiple image fields (image1, image2, image3, image4)
        ['image1', 'image2', 'image3', 'image4'].forEach((imgKey, imgIndex) => {
          if (section?.[imgKey]) {
            processAndAppendImage(section[imgKey], `${sectionId}_${imgKey}`, i);
          }
        });
        
        // Handle features array images (for components like 2COL_3ROWS_Benefits)
        if (section?.features && Array.isArray(section.features)) {
          section.features.forEach((feature, featureIndex) => {
            // Process image field (preferred)
            if (feature?.image) {
              processAndAppendImage(feature.image, `${sectionId}_feature_${featureIndex}_image`, i);
            }
            // Also process image_placeholder if image is not set
            else if (feature?.image_placeholder) {
              processAndAppendImage(feature.image_placeholder, `${sectionId}_feature_${featureIndex}_image`, i);
            }
          });
        }
      }

      // 10. Send the request
      const response = await axiosInstance.post(
        "/api/v1/email_templates",
        formData
      );

      if (response.data && response.data.template) {
        localStorage.setItem("last_saved_draft_id", response.data.template.id);
        
        // Update template name state
        setDraftTemplateName(nameToSave || "Draft Email");
        
        if (showSuccessToast) {
          toast.success("Draft saved successfully");
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    }
    setSavedDraft(true);
    setIsSavingDraft(false);
  };

  const bottomRef = useRef();

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [streamingSections]);

  const [tone, setTone] = useState("");

  return (
    // <EmailStyleProvider
    //   key={`style-provider-${styleVersion}`}
    //   value={{
    //     primaryColor,
    //     secondaryColor,
    //     fontFamily,
    //     buttonTextColor: "#ffffff",
    //   }}
    // >
    //    </EmailStyleProvider>
    <>
    <ToastContainer />
      <EmailSectionProvider initialSections={initialSections}>
        <div>
          <div className="position-relative">

            {/* {isLoading && streamingSections.length === 0 && (
              <div
                className="loader-overlay"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100%",
                  background: "rgba(0, 0, 0, 0.5)",
                  backdropFilter: "blur(5px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 9999,
                  pointerEvents: "all",
                  overflow: "hidden",
                }}
              >
                <Loader />
              </div>
            )} */}

            <Breadcrumb />

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="title-label">Create Email</div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={toggleStyleSettings}
                  title="Email Styles"
                >
                  <Settings size={18} />
                </button>
                
                <button className="btn bgprimary" onClick={handleShowDraftModal} disabled={isSavingDraft}>
                  {isSavingDraft ? "Saving..." : "Save as draft"}
                </button>
                {/* onClick={handlePushToShop} */}
                {/* <button
                  className="btn btn-dark"
                  onClick={handlePushToShop}
                >
                  Push to shop
                </button> */}
                {/* <div className="dropdown">
                  <button
                    className="btn btn-outline-secondary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Share
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <a className="dropdown-item" href="#">
                        Copy Link
                      </a>
                    </li>
                    <li>
                      <a className="dropdown-item" href="#">
                        Share via Email
                      </a>
                    </li>
                  </ul>
                </div> */}
              </div>
            </div>

            {showStyleSettings && (
              <div
                ref={styleSettingsRef}
                className="style-settings-panel p-3 bg-white border rounded shadow-sm"
                style={{
                  position: "absolute",
                  right: "20px",
                  top: "80px",
                  zIndex: 1000,
                  width: "300px",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Style Settings</h5>
                  <button
                    className="btn btn-link p-0"
                    onClick={toggleStyleSettings}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="mb-3">
                  <label className="form-label">Primary Color (Content)</label>
                  <div
                    className="color-preview d-flex align-items-center"
                    onClick={() => toggleColorPicker("primary")}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      style={{
                        backgroundColor: primaryColor,
                        width: "30px",
                        height: "30px",
                        borderRadius: "4px",
                        marginRight: "10px",
                        border: "1px solid #ddd",
                      }}
                    ></div>
                    <span>{primaryColor}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Secondary Color (Headings)
                  </label>
                  <div
                    className="color-preview d-flex align-items-center"
                    onClick={() => toggleColorPicker("secondary")}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      style={{
                        backgroundColor: secondaryColor,
                        width: "30px",
                        height: "30px",
                        borderRadius: "4px",
                        marginRight: "10px",
                        border: "1px solid #ddd",
                      }}
                    ></div>
                    <span>{secondaryColor}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Font Family</label>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="font-family-dropdown"
                      className="w-100 text-start"
                    >
                      {fontOptions.find((f) => f.value === fontFamily)?.name ||
                        "Select Font"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100">
                      {fontOptions.map((font) => (
                        <Dropdown.Item
                          key={font.value}
                          onClick={() => setFontFamily(font.value)}
                          style={{ fontFamily: font.value }}
                        >
                          {font.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {showColorPicker && (
                  <div
                    ref={colorPickerRef}
                    style={{
                      position: "absolute",
                      zIndex: 1001,
                      top: activeColorPicker === "primary" ? "95px" : "155px",
                      right: "20px",
                    }}
                  >
                    <ChromePicker
                      color={
                        activeColorPicker === "primary"
                          ? primaryColor
                          : secondaryColor
                      }
                      onChange={handleColorChange}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="row">
              <div className="generated-product-left-container col-md-4">
                <CreateEmailLeft
                  onGenerateEmail={handleGenerateEmail}
                  previewImage={previewImage || ""}
                  previewUrl={previewUrl || ""}
                  templateName={templateName}
                  templateId={templateId}
                  setIsLoading={setIsLoading}
                  isLoading={isLoading}
                  onEmailChunk={handleEmailChunk}
                  clearStreamingSections={clearStreamingSections}
                  tone={tone}
                  setTone={setTone}
                  occasion={occasion}
                  setOccasion={setOccasion}
                  furtherDetails={furtherDetails}
                  setFurtherDetails={setFurtherDetails}
                  uid={uid}
                />
              </div>

              <div className="generated-product-right-container col-md-8">
              <div className="d-flex gap-4 align-items-center" >
            <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
              Generated Email
            </h5>
          <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">

            <h6 className="mt-2 mx-1">Preview</h6>
            <div className="d-flex justify-content-center align-items-center">
              <button className="btn" onClick={() => handlePreview("desktop")}>
                <BiLaptop size={20} />
              </button> |
              <button className="btn" onClick={() => handlePreview("mobile")}>
                <BiMobile size={20} />
              </button>
            </div>

          </div>
          
        </div>
                <EmailSectionProvider
                  initialSections={
                    streamingSections.length > 0
                      ? streamingSections
                      : initialSections
                  }
                >
                  <EmailDetailsContainer
                    isLoading={isLoading}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    fontFamily={fontFamily}
                    onUpdateEmailData={handleUpdateEmailData}
                  />
                </EmailSectionProvider>
              </div>
            </div>
          </div>
        </div>
      </EmailSectionProvider>

      <LocalDraftModal
        show={showLocalDraftModal}
        onHide={() => setShowLocalDraftModal(false)}
        onConfirm={handleLocalDraftConfirm}
        isChecking={isSavingDraft}
        showUpdateOption={savedDraft}
      />
    </>
  );
};

export default CreateEmail;